// src/pages/admin/UserManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Form,
  Button,
  Modal,
  Alert,
  Badge,
  Spinner,
  InputGroup,
  FormControl
} from 'react-bootstrap';
import apiClient from '../../../utils/axios';
import { useAuth } from '../../auth/AuthContext';
// 導入排序圖標
import { BsSortAlphaDown, BsSortAlphaUp, BsArrowUp, BsArrowDown } from 'react-icons/bs';

const UserManagement = () => {
  // 移除空對象模式，直接不使用 useAuth() 的返回值
  useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // 新增排序狀態
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // 新增額外資訊模態框狀態
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoContent, setInfoContent] = useState('');
  const [infoUserName, setInfoUserName] = useState('');

  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activateUserId, setActivateUserId] = useState(null);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectUserId, setRejectUserId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');

  // 新增排序處理函數
  const handleSort = (field) => {
    if (sortField === field) {
      // 如果點擊當前排序欄位，則切換排序方向
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 如果點擊新欄位，預設使用升序排序
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // 根據當前排序設置對使用者資料進行排序
  const sortedUsers = [...users].sort((a, b) => {
    if (sortField === 'id') {
      return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
    } else if (sortField === 'username') {
      return sortDirection === 'asc' 
        ? a.username.localeCompare(b.username) 
        : b.username.localeCompare(a.username);
    } else if (sortField === 'email') {
      return sortDirection === 'asc' 
        ? a.email.localeCompare(b.email) 
        : b.email.localeCompare(a.email);
    } else if (sortField === 'registrationDate') {
      return sortDirection === 'asc' 
        ? new Date(a.registrationDate) - new Date(b.registrationDate) 
        : new Date(b.registrationDate) - new Date(a.registrationDate);
    }
    return 0;
  });

  // 查看額外資訊的處理函數
  const handleViewInfo = (info, username) => {
    setInfoContent(info || '無額外資訊');
    setInfoUserName(username);
    setShowInfoModal(true);
  };

  // 載入使用者列表
  const loadUsers = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.get(`/admin/users`, {
        params: {
          page,
          search: searchTerm,
          sort: sortField,
          direction: sortDirection
        }
      });
      setUsers(resp.data.users || []);
      setCurrentPage(resp.data.currentPage || page);
      setTotalPages(resp.data.totalPages || 1);
    } catch (err) {
      setError('無法加載使用者：' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [searchTerm, sortField, sortDirection]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = e => {
    e.preventDefault();
    loadUsers(1);
  };

  const handlePageChange = page => {
    loadUsers(page);
  };

  const handleActivateUser = async () => {
    try {
      await apiClient.patch(`/admin/users/${activateUserId}/status`, {
        status: 'active',
        expireDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
      });
      setShowActivateModal(false);
      loadUsers(currentPage);
    } catch (err) {
      setError('啟用失敗：' + (err.response?.data?.message || err.message));
    }
  };

  const handleRejectUser = async () => {
    if (!rejectReason.trim()) {
      setError('請輸入拒絕原因');
      return;
    }
    try {
      await apiClient.patch(`/admin/users/${rejectUserId}/status`, {
        status: 'rejected',
        rejectReason
      });
      setShowRejectModal(false);
      setRejectReason('');
      loadUsers(currentPage);
    } catch (err) {
      setError('拒絕失敗：' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteReason.trim()) {
      setError('請輸入刪除原因');
      return;
    }
    try {
      await apiClient.delete(`/admin/users/${deleteUserId}`, {
        data: { deleteReason }
      });
      setShowDeleteModal(false);
      setDeleteReason('');
      loadUsers(currentPage);
    } catch (err) {
      setError('刪除失敗：' + (err.response?.data?.message || err.message));
    }
  };

  // 分頁按鈕
  const Pagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          variant={i === currentPage ? 'primary' : 'outline-primary'}
          className="mx-1"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }
    return (
      <div className="d-flex justify-content-center my-3">
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          上一頁
        </Button>
        {pages}
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          下一頁
        </Button>
      </div>
    );
  };

  return (
    <div className="user-management p-4">
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <h3 className="mb-0">使用者管理</h3>

        <Form onSubmit={handleSearch} className="d-flex">
          <InputGroup>
            <FormControl
              placeholder="搜尋使用者..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button variant="primary" type="submit">
              搜尋
            </Button>
          </InputGroup>
        </Form>
      </div>

      {/* 加載狀態 */}
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary" />
          <p className="mt-3">正在加載使用者數據...</p>
        </div>
      ) : (
        <>
          {/* 桌面版表格 - 在平板和大屏幕上顯示 */}
          <div className="d-none d-md-block table-responsive">
            <Table hover className="align-middle border">
              <thead className="bg-light">
                <tr>
                  <th onClick={() => handleSort('username')}>
                    使用者名稱
                    {sortField === 'username' && (
                      sortDirection === 'asc' ? <BsSortAlphaDown /> : <BsSortAlphaUp />
                    )}
                  </th>
                  <th onClick={() => handleSort('email')}>
                    電子郵件
                    {sortField === 'email' && (
                      sortDirection === 'asc' ? <BsSortAlphaDown /> : <BsSortAlphaUp />
                    )}
                  </th>
                  <th onClick={() => handleSort('registrationDate')}>
                    註冊日期
                    {sortField === 'registrationDate' && (
                      sortDirection === 'asc' ? <BsArrowUp /> : <BsArrowDown />
                    )}
                  </th>
                  <th>額外資訊</th>
                  <th>帳戶狀態</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">
                      沒有找到使用者
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map(u => (
                    <tr key={u.id}>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>
                        {new Date(u.registrationDate).toLocaleDateString()}
                      </td>
                      <td style={{ maxWidth: 150 }}>
                        <Button
                          size="sm"
                          variant="info"
                          onClick={() => handleViewInfo(u.additionalInfo, u.username)}
                        >
                          查看
                        </Button>
                      </td>
                      <td>
                        {u.isActive ? (
                          <Badge bg="success">已啟用</Badge>
                        ) : (
                          <Badge bg="warning">待審核</Badge>
                        )}
                      </td>
                      <td>
                        {!u.isActive ? (
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => {
                                setActivateUserId(u.id);
                                setShowActivateModal(true);
                              }}
                            >
                              啟用
                            </Button>
                            <Button
                              size="sm"
                              variant="warning"
                              onClick={() => {
                                setRejectUserId(u.id);
                                setShowRejectModal(true);
                              }}
                            >
                              拒絕
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setDeleteUserId(u.id);
                              setShowDeleteModal(true);
                            }}
                          >
                            刪除
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* 行動裝置版卡片 */}
          <div className="d-md-none">
            {sortedUsers.map(u => (
              <Card key={u.id} className="mb-3">
                <Card.Body>
                  <Card.Title>{u.username}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    {u.email}
                  </Card.Subtitle>
                  <p className="mb-1">
                    <small className="text-muted">註冊：</small>
                    {new Date(u.registrationDate).toLocaleDateString()}
                  </p>
                  <p className="mb-1">
                    <small className="text-muted">資訊：</small>{' '}
                    {u.additionalInfo || '無'}
                  </p>
                  <p className="mb-2">
                    <small className="text-muted">狀態：</small>{' '}
                    {u.isActive ? (
                      <Badge bg="success">已啟用</Badge>
                    ) : (
                      <Badge bg="warning">待審核</Badge>
                    )}
                  </p>
                  {!u.isActive ? (
                    <div className="d-grid gap-2">
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => {
                          setActivateUserId(u.id);
                          setShowActivateModal(true);
                        }}
                      >
                        啟用帳戶
                      </Button>
                      <Button
                        size="sm"
                        variant="warning"
                        onClick={() => {
                          setRejectUserId(u.id);
                          setShowRejectModal(true);
                        }}
                      >
                        拒絕申請
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="danger"
                      className="w-100"
                      onClick={() => {
                        setDeleteUserId(u.id);
                        setShowDeleteModal(true);
                      }}
                    >
                      刪除帳戶
                    </Button>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>

          {/* 分頁 */}
          {users.length > 0 && <Pagination />}
        </>
      )}

      {/* 啟用 Modal */}
      <Modal show={showActivateModal} onHide={() => setShowActivateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>確認啟用帳戶</Modal.Title>
        </Modal.Header>
        <Modal.Body>確定要啟用此用戶嗎？</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActivateModal(false)}>
            取消
          </Button>
          <Button variant="success" onClick={handleActivateUser}>
            確認
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 拒絕 Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>拒絕用戶申請</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="rejectReason">
            <Form.Label>拒絕原因</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            取消
          </Button>
          <Button variant="warning" onClick={handleRejectUser}>
            確認
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 刪除 Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>刪除用戶帳戶</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="deleteReason">
            <Form.Label>刪除原因</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            取消
          </Button>
          <Button variant="danger" onClick={handleDeleteUser}>
            確認
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 額外資訊 Modal */}
      <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{infoUserName} 的額外資訊</Modal.Title>
        </Modal.Header>
        <Modal.Body>{infoContent}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInfoModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;